"use client";

import { useEhrExtensionBridge } from "@/hooks/recording-lease";
import { usePostHogCapture } from "@/hooks/use-posthog-capture";
import type { CompletedRecording } from "@medimo/transcription-summary";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { type PropsWithChildren, useCallback } from "react";
import { isAuthProtectedPathname } from "./auth-page";

// 録音資源（AudioContext / WebSocket / 音声処理）は (dashboard)/layout.tsx に
// マウントされていたが、録音中に別診察へ遷移すると App Router が (dashboard)
// セグメントを作り直し、その度に Provider ごと録音が破棄されていた（WEB-212）。
// 引き金（なぜ作り直されるか）に依存せず潰すため、録音 Provider を
// クライアント遷移では作り直されない root 直下へ引き上げる。root レイアウトは
// フルロード / global-error 以外では再マウントされないため、セグメントが
// 作り直されても録音資源は生き残る。
//
// サインイン画面（/auth 配下）ではマイク列挙などのマウント時副作用を走らせない
// よう、認証エリアのパスでのみ実体化する。認証エリア内（診察間）の遷移では
// 条件が真のまま保たれるため Provider は再マウントされない。
//
// ミニ画面(PiP)の GlobalPipWindow も同様に (dashboard) 側に残すと遷移で作り直され、
// component-local に保持している PiP ウィンドウのハンドルが失われて閉じる/チラつく。
// 録音 Provider と一緒に root へ置くことで、遷移してもミニ画面が維持される。
const SharedAudioContextProvider = dynamic(
  () => import("@medimo/audio").then((mod) => mod.SharedAudioContextProvider),
  { ssr: false },
);
const TranscriptionEditorProvider = dynamic(
  () =>
    import("@medimo/transcription-summary").then(
      (mod) => mod.TranscriptionEditorProvider,
    ),
  { ssr: false },
);
const MicrophoneProvider = dynamic(
  () => import("@medimo/microphone").then((mod) => mod.MicrophoneProvider),
  { ssr: false },
);
const GlobalPipWindow = dynamic(
  () =>
    import("@/app/(dashboard)/components/global-pip-window").then(
      (mod) => mod.GlobalPipWindow,
    ),
  { ssr: false },
);

export function GlobalRecordingProviders({ children }: PropsWithChildren) {
  const pathname = usePathname();
  // 電子カルテ連携の拡張機能がいるかの知らせは、録音を始める前から受け取っておく。
  // 録音の開始で初めて受け付けると、拡張機能に問わずに始めることになる。
  useEhrExtensionBridge();
  const capturePostHog = usePostHogCapture();
  const captureRecordingDuration = useCallback(
    ({ consultId, recordingIndex, duration }: CompletedRecording) => {
      // index = 1 は初回録音、index > 1 は追加録音
      capturePostHog("recording_duration_recorded", {
        consult_id: consultId,
        index: recordingIndex,
        duration,
      });
    },
    [capturePostHog],
  );

  // ゲートは「/auth 以外」の広い条件を意図的に採用している。特定の (dashboard)
  // ルート接頭辞で絞ると、未登録/新規の認証エリアのルートへ遷移した瞬間に
  // Provider が外れて録音が破棄され、本 PR が直す WEB-212 が再発する。広い条件は
  // 認証エリア内のあらゆる遷移で Provider を維持できる代わりに、/auth 以外の
  // 非ダッシュボード経路(404 等)でも実体化する。ただし Provider は認証エリアに
  // 入った時点で一度だけマウントされ以後は維持されるため、マイク列挙が走るのは
  // 「最初のロードが 404」という稀なケースに限られ、影響は一過性で軽微。
  if (!isAuthProtectedPathname(pathname)) {
    return <>{children}</>;
  }

  return (
    <SharedAudioContextProvider>
      <TranscriptionEditorProvider
        onRecordingCompleted={captureRecordingDuration}
      >
        <MicrophoneProvider>
          {children}
          <GlobalPipWindow />
        </MicrophoneProvider>
      </TranscriptionEditorProvider>
    </SharedAudioContextProvider>
  );
}
